import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/database';

type Tables = Database['public']['Tables'];
export type NotificationRow = Tables['notifications']['Row'];

/**
 * Rows have been written since Phase 8 (`results.published`,
 * `session.cancelled`, `attempt.awaiting_grading`, `attempt.grading_failed`,
 * `institute_approved`, `institute_rejected`) but nothing ever read them.
 * RLS allows a user to SELECT and UPDATE only their own rows — there is no
 * INSERT or DELETE policy, so "mark as read" is supported and "dismiss" is
 * not.
 */
export const NOTIFICATION_QUERY_KEYS = {
  list: (userId: string, limit: number) => ['notifications', 'list', userId, limit] as const,
  unreadCount: (userId: string) => ['notifications', 'unread_count', userId] as const,
};

// Every read filters on user_id explicitly. RLS alone is NOT enough here:
// `notifications_self_read` is `auth.uid() = user_id OR is_superadmin()`, so an
// unfiltered select would show a superadmin everyone else's notifications in
// their own feed. (The UPDATE policy has no superadmin clause, so the
// mark-read writes below are already correctly scoped.)

/** Recent notifications for one user, newest first. */
export async function listNotifications(userId: string, limit: number): Promise<NotificationRow[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error !== null) throw new Error(error.message);
  return data ?? [];
}

/**
 * Unread badge count. Shaped to hit `notifications_user_unread_idx` — the
 * partial index on (user_id, created_at desc) where read_at is null.
 */
export async function getUnreadCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .is('read_at', null);
  if (error !== null) throw new Error(error.message);
  return count ?? 0;
}

export async function markRead(id: number): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id)
    .is('read_at', null);
  if (error !== null) throw new Error(error.message);
}

/** One bulk UPDATE — allowed by the notifications_self_update policy. */
export async function markAllRead(): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null);
  if (error !== null) throw new Error(error.message);
}
